// Tour content - all tooltip titles and descriptions in one place
export const tourContent = {
  // Code Editor Page
  codeEditor: {
    downloadRepository: {
      title: 'Download Repository - Export Project',
      content: 'Download your complete project repository as a ZIP file for backup, sharing, or local development.',
      label: 'EXPORT OPTIONS',
      labelText: 'ZIP download, project backup, local development, sharing',
      tip: 'Perfect for creating backups or working offline on your project'
    },
    
    expandCommit: {
      title: 'View Commit Details - File Changes',
      content: 'Click to expand and view the detailed file changes, additions, and deletions for this commit.',
      label: 'COMMIT DETAILS',
      labelText: 'File changes, code diff, commit analysis, change tracking',
      tip: 'See exactly what files were modified in each commit'
    },
    
    resetToCommit: {
      title: 'Reset to Commit - Revert Changes',
      content: 'Reset your repository to this specific commit, permanently removing all subsequent changes.',
      label: 'DANGER ZONE',
      labelText: 'Permanent reset, change removal, commit reversion, data loss',
      tip: 'This action cannot be undone - use with extreme caution'
    },
    fileExplorer: {
      title: 'Project File Navigator - File Management',
      content: 'Browse your complete project structure with file tree navigation, search capabilities, and quick file access.',
      label: 'BEST FOR',
      labelText: 'File navigation, project structure, code organization, file search',
      tip: 'Use the search function to quickly locate specific files or code snippets'
    },
    
    refreshFiles: {
      title: 'Refresh File Tree',
      content: 'Reload the project file structure to reflect any changes made outside the editor.',
      label: 'UPDATES',
      labelText: 'File system sync, external changes, new files detection',
      tip: 'Use this when files are added, removed, or modified externally'
    },
    
    saveFile: {
      title: 'Save File Changes',
      content: 'Save your current file modifications to disk. Changes are preserved and ready for deployment.',
      label: 'SHORTCUT',
      labelText: 'File persistence, change saving, keyboard shortcut ⌘S',
      tip: 'Files auto-save on focus change, but manual save ensures immediate persistence'
    }
  },

  // Header Component
  header: {
    // Main Navigation Buttons
    chatNavButton: {
      title: 'Chat Interface',
      content: 'Switch to the main chat interface where you can communicate with AI agents and manage conversations.',
      label: 'NAVIGATION',
      labelText: 'Project conversations, agent interactions, thread management',
      tip: 'This is your primary workspace for AI-powered development'
    },
    
    codeNavButton: {
      title: 'Code Editor',
      content: 'View and edit your project files. Browse folders, open multiple files in tabs, save your changes, and download your code.',
      label: 'BEST FOR',
      labelText: 'Viewing files, making edits, saving changes, downloading code',
      tip: 'Simple file editor for viewing, modifying, and downloading your project files'
    },
    
    securityNavButton: {
      title: 'Security Dashboard',
      content: 'Monitor security vulnerabilities, code analysis, and compliance status for your project.',
      label: 'SECURITY',
      labelText: 'Vulnerability scanning, code analysis, compliance checks',
      tip: 'Available in localhost development environment'
    },
    
    // Thread Navigation Buttons
    projectToThreadButton: {
      title: 'Go to Last Thread',
      content: 'Navigate to your most recently visited thread in this project. If no thread was visited yet, takes you to the main project workspace.',
      label: 'QUICK ACCESS',
      labelText: 'Resume conversations, return to active discussions, thread history',
      tip: 'Quickly jump back into your ongoing conversations and development discussions'
    },
    
    backToProjectButton: {
      title: 'Project Navigation',
      content: 'Navigate within your current project between the main workspace and your active threads. Click to switch between file management and conversation views.',
      label: 'NAVIGATION FUNCTION',
      labelText: 'From workspace to last active thread, or from thread back to workspace',
      tip: 'Use this to access file uploads or return to your ongoing conversations'
    },
    
    threadChatTab: {
      title: 'Conversation Interface',
      content: 'Your primary workspace for natural language interaction with Kolega.dev.',
      label: 'BEST FOR',
      labelText: 'Planning, brainstorming, and iterative development',
      tip: 'Speak naturally - Kolega understands context and nuance'
    },
    
    threadTasksTab: {
      title: 'Task Management',
      content: 'Track and manage tasks, subtasks, and project milestones within this thread.',
      label: 'PRODUCTIVITY',
      labelText: 'Task tracking, progress monitoring, milestone management',
      tip: 'Organize work items and track development progress'
    },
    
    threadCodeTab: {
      title: 'Code Editor',
      content: 'Direct access to your project files and code editing',
      label: 'WHEN TO USE',
      labelText: 'Fine-tuning code, reviewing implementations, manual edits',
      tip: 'Switch seamlessly between Chat and Code Editor for optimal workflow'
    },
    
    // User Interface Controls
    userMenuButton: {
      title: 'Account & Settings',
      content: 'Access your profile, project settings, manage your projects and account management options.',
      label: 'QUICK ACCESS',
      labelText: 'Project settings, account preferences, administration, payment and plan options',
      tip: 'Project Settings contains deployment, environments and infrastructure options'
    },
    
    darkModeToggle: {
      title: 'Theme Toggle',
      content: 'Switch between light and dark themes to customize your visual experience.',
      label: 'APPEARANCE',
      labelText: 'Light mode, dark mode, visual preferences',
      tip: 'Theme preference is saved automatically across sessions'
    },
    
    sandboxStatus: {
      title: 'Workspace Status',
      content: 'Shows the current status of your development workspace environment. Click to retry if there are errors.',
      label: 'DEVELOPMENT',
      labelText: 'Environment status, workspace health, error recovery',
      tip: 'Green means ready, yellow means loading, red means error - click red to retry'
    },
    
    helpButton: {
      title: 'Help & Support',
      content: 'Access documentation, tutorials, and support resources for Kolega.dev.',
      label: 'QUICK ACCESS',
      labelText: 'Keyboard shortcuts, feature guides, contact support',
      tip: 'Press "?" for keyboard shortcuts anytime'
    },
    
    // Bug Report System
    bugReportButton: {
      title: 'Bug Report',
      content: 'Report bugs, UI issues, or feature requests directly to the development team. Help improve Kolega.dev by sharing your feedback.',
      label: 'FEEDBACK',
      labelText: 'Bug reporting, issue tracking, feature requests, user feedback',
      tip: 'Include screenshots and detailed descriptions for faster resolution'
    },
    
    bugReportIssueType: {
      title: 'Issue Type Selection',
      content: 'Select the type of issue you\'re experiencing. Choose from bug/error, UI/design issues, feature requests, or performance problems.',
      label: 'CATEGORIZATION',
      labelText: 'Bug classification, issue types, problem categorization',
      tip: 'Accurate categorization helps prioritize and route your report to the right team'
    },
    
    bugReportDescription: {
      title: 'Issue Description',
      content: 'Provide a detailed description of the issue. Include steps to reproduce, expected behavior, and actual behavior for bugs.',
      label: 'DETAILS',
      labelText: 'Problem description, reproduction steps, expected vs actual behavior',
      tip: 'The more detail you provide, the faster we can identify and fix the issue'
    },
    
    bugReportScreenshot: {
      title: 'Screenshot Upload',
      content: 'Upload a screenshot to help illustrate the issue. Visual evidence greatly helps in understanding and reproducing problems.',
      label: 'VISUAL EVIDENCE',
      labelText: 'Screenshot upload, visual documentation, error illustrations',
      tip: 'Screenshots are especially helpful for UI issues and error messages'
    },
    
    bugReportSubmit: {
      title: 'Submit Report',
      content: 'Submit your bug report to the development team. You\'ll receive confirmation and the report will be tracked for resolution.',
      label: 'SUBMISSION',
      labelText: 'Report submission, feedback tracking, issue resolution',
      tip: 'Reports are automatically enriched with system information to help with debugging'
    }
  },

  // Project Settings Page
  projectSettings: {
    backToProject: {
      title: 'Return to Project - Navigation',
      content: 'Navigate back to your project workspace to continue development and access all project features.',
      label: 'NAVIGATION METHOD',
      labelText: 'Quick return to project dashboard, workspace access',
      tip: 'Use this to quickly return to your main project workspace'
    },
    settingsGeneral: {
      title: 'General Settings - Project Configuration',
      content: 'Configure basic project settings including name, description, and general preferences.',
      label: 'BEST FOR',
      labelText: 'Project metadata, basic configuration, general preferences',
      tip: 'Start here for fundamental project setup and information'
    },
    settingsDelete: {
      title: 'Delete Project - Permanent Removal',
      content: 'Permanently remove your project with a 30-day recovery period. Handle with care.',
      label: 'DANGER ZONE',
      labelText: 'Project deletion, permanent removal, recovery options',
      tip: 'Projects can be restored within 30 days after deletion'
    },
    deleteProjectButton: {
      title: 'Delete Project - Permanent Action',
      content: 'Permanently delete this project. You will have 30 days to restore it before permanent removal.',
      label: 'DANGER ZONE',
      labelText: 'Permanent deletion, 30-day recovery period, irreversible action',
      tip: 'This action cannot be undone immediately - use with extreme caution'
    }
  },

  // Account Settings
  account: {
    // Navigation sections
    accountProfile: {
      title: 'Profile Settings - Personal Information',
      content: 'Manage your personal information, display name, email address, and basic account preferences.',
      label: 'MANAGE',
      labelText: 'Personal details, display name, email, profile picture',
      tip: 'Start here to update your basic account information'
    },
    accountPassword: {
      title: 'Password Settings - Security Management',
      content: 'Change your account password and manage password security settings.',
      label: 'SECURITY',
      labelText: 'Password changes, security updates, account protection',
      tip: 'Use a strong, unique password for better security'
    },
    accountPlan: {
      title: 'Plan Settings - Subscription Management',
      content: 'View and manage your subscription plan, upgrade or downgrade options, and plan features.',
      label: 'SUBSCRIPTION',
      labelText: 'Plan upgrades, feature access, subscription management',
      tip: 'Compare plans to find the best fit for your needs'
    },
    accountCredits: {
      title: 'Credits Management - Usage Tracking',
      content: 'Monitor your credit usage, view remaining credits, and manage credit-based features.',
      label: 'USAGE TRACKING',
      labelText: 'Credit balance, usage history, feature consumption',
      tip: 'Keep track of your usage to avoid service interruptions'
    },
    accountBilling: {
      title: 'Billing Settings - Payment Management',
      content: 'Manage payment methods, view billing history, download invoices, and update billing information.',
      label: 'PAYMENTS',
      labelText: 'Payment methods, billing history, invoices, payment updates',
      tip: 'Keep your payment information current to avoid service disruptions'
    },
    accountNotifications: {
      title: 'Notification Settings - Communication Preferences',
      content: 'Configure email notifications, alerts, and communication preferences for your account.',
      label: 'COMMUNICATION',
      labelText: 'Email alerts, notification preferences, communication settings',
      tip: 'Customize notifications to stay informed without being overwhelmed'
    },
    accountSecurity: {
      title: 'Security Settings - Account Protection',
      content: 'Manage two-factor authentication, view login history, and configure advanced security settings.',
      label: 'PROTECTION',
      labelText: 'Two-factor auth, login history, security monitoring',
      tip: 'Enable 2FA for enhanced account security'
    },
    
    // Action buttons
    editProfileButton: {
      title: 'Edit Profile - Personal Information',
      content: 'Modify your personal information, display name, and account preferences.',
      label: 'BEST FOR',
      labelText: 'Name changes, email updates, profile customization',
      tip: 'Email changes require verification before taking effect'
    },
    saveProfileButton: {
      title: 'Save Profile - Confirm Changes',
      content: 'Save your profile changes and update your account information.',
      label: 'ACTION REQUIRED',
      labelText: 'Profile updates, information confirmation, account changes',
      tip: 'Email changes will require verification via confirmation code'
    },
    verifyEmailButton: {
      title: 'Verify Email - Confirm Address',
      content: 'Verify your new email address using the confirmation code sent to your inbox.',
      label: 'SECURITY STEP',
      labelText: 'Email verification, account security, address confirmation',
      tip: 'Check your email inbox and spam folder for the verification code'
    },
    changePasswordButton: {
      title: 'Change Password - Security Update',
      content: 'Update your account password with a new secure password meeting all requirements.',
      label: 'SECURITY REQUIREMENTS',
      labelText: '8+ characters, uppercase, lowercase, numbers, special characters',
      tip: 'Use a strong, unique password that you do not use elsewhere'
    },
    saveNotificationsButton: {
      title: 'Save Notifications - Preference Update',
      content: 'Save your notification preferences for email, push, and in-app notifications.',
      label: 'NOTIFICATION TYPES',
      labelText: 'Email alerts, push notifications, in-app messages, preferences',
      tip: 'Customize which notifications you receive and how you receive them'
    },
    toggleMfaButton: {
      title: 'Multi-Factor Authentication - Security Toggle',
      content: 'Enable or disable two-factor authentication for enhanced account security. When enabled, you will need both your password and a verification code to sign in.',
      label: 'SECURITY ENHANCEMENT',
      labelText: 'Account protection, login security, authentication verification',
      tip: 'Enabling MFA significantly increases your account security - highly recommended'
    },
    cancelMfaSetup: {
      title: 'Cancel MFA Setup - Exit Configuration',
      content: 'Cancel the multi-factor authentication setup process and return to security settings.',
      label: 'SETUP CONTROL',
      labelText: 'Setup cancellation, configuration exit, security settings return',
      tip: 'You can restart MFA setup anytime from the security settings'
    },
    verifyMfaCode: {
      title: 'Verify MFA Code - Complete Setup',
      content: 'Enter the 6-digit verification code from your authenticator app to complete MFA setup.',
      label: 'VERIFICATION STEP',
      labelText: 'Code verification, authenticator app, setup completion',
      tip: 'Use Google Authenticator, Authy, or similar apps to generate the code'
    },
    showMoreActivity: {
      title: 'Show Activity History - Authentication Log',
      content: 'View complete authentication activity history including login attempts, locations, and security events.',
      label: 'SECURITY MONITORING',
      labelText: 'Login history, security events, account activity, threat detection',
      tip: 'Review regularly to detect any unauthorized access attempts'
    },
    refreshCredits: {
      title: 'Refresh Credits - Update Balance',
      content: 'Refresh your credit balance and usage statistics to see the most current information.',
      label: 'ACCOUNT SYNC',
      labelText: 'Balance updates, usage refresh, credit synchronization',
      tip: 'Use this if your balance seems outdated or after making a purchase'
    },
    selectPlanButton: {
      title: 'Select Plan - Subscription Management',
      content: 'Choose or change your subscription plan to access different features and credit allowances.',
      label: 'PLAN FEATURES',
      labelText: 'Subscription tiers, feature access, billing management',
      tip: 'Compare features carefully to choose the plan that best fits your needs'
    },
    contactSalesButton: {
      title: 'Contact Sales - Enterprise Support',
      content: 'Get in touch with our sales team for enterprise plans, custom solutions, and volume pricing.',
      label: 'ENTERPRISE OPTIONS',
      labelText: 'Custom pricing, volume discounts, enterprise features, dedicated support',
      tip: 'Perfect for teams and organizations with specific requirements'
    },
    addPaymentMethod: {
      title: 'Add Payment Method - Billing Setup',
      content: 'Add a new credit card or payment method to your account for subscriptions and credit purchases.',
      label: 'PAYMENT OPTIONS',
      labelText: 'Credit cards, billing setup, payment security',
      tip: 'Your payment information is securely encrypted and stored'
    },
    editPaymentMethod: {
      title: 'Edit Payment Method - Update Details',
      content: 'Update your existing payment method details, including card information and billing address.',
      label: 'PAYMENT UPDATES',
      labelText: 'Card updates, billing address, payment modifications',
      tip: 'Keep your payment information current to avoid service interruptions'
    },
    removePaymentMethod: {
      title: 'Remove Payment Method - Delete Card',
      content: 'Remove this payment method from your account. Ensure you have another method if you have active subscriptions.',
      label: 'PAYMENT REMOVAL',
      labelText: 'Card deletion, payment cleanup, billing management',
      tip: 'You need at least one payment method for active subscriptions'
    },
    updateBillingInfo: {
      title: 'Update Billing Info - Account Details',
      content: 'Update your billing information including company name, address, and tax details.',
      label: 'BILLING DETAILS',
      labelText: 'Company info, billing address, tax information, invoice details',
      tip: 'Accurate billing information ensures proper invoicing and tax compliance'
    },
    downloadInvoice: {
      title: 'Download Invoice - Billing Records',
      content: 'Download a PDF copy of this invoice for your records, accounting, or expense reporting.',
      label: 'INVOICE MANAGEMENT',
      labelText: 'PDF download, billing records, expense tracking, accounting',
      tip: 'Keep invoices for tax purposes and expense reimbursement'
    }
  }
};
